"use client";

/**
 * SetupLayout Component
 *
 * Split-screen layout wrapper for the setup flow:
 * - Desktop (lg+): 55% form (left) / 45% social proof (right)
 * - Mobile: Full-width form, social proof hidden
 * - Payment step: Full-width form, social proof hidden
 *
 * IMPORTANT: Layout changes are INSTANT (no animation).
 * Only content animates via BlurTransition. This prevents
 * horrible mid-transition states when switching layouts.
 *
 * Header design (Fyxer.ai style):
 * - Row 1: Back button (left) | Logo (centered) | empty (right)
 * - Row 2: Full-width segmented progress bar (only from step 1+)
 */
import { c as _c } from "react/compiler-runtime";
import { ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import SocialProofPanel from "./SocialProofPanel";
import type { SetupStep } from "../types";
interface SetupLayoutProps {
  children: ReactNode;
  /** Current step for the indicator */
  currentStep: SetupStep;
  /** Total number of steps */
  totalSteps: number;
  /** Whether to show the back button */
  showBackButton?: boolean;
  /** Back button handler */
  onBack?: () => void;
  /** Custom label for the back button (default: "Back") */
  backButtonLabel?: string;
  /** Whether user is logged in (affects step count) */
  isLoggedIn?: boolean;
  /** Whether to use full-width layout (hides social proof panel) */
  fullWidth?: boolean;
}

// Shared transition config
const TRANSITION = {
  duration: 0.25,
  ease: [0.22, 1, 0.36, 1] as const
};
export default function SetupLayout(t0) {
  const $ = _c(43);
  const {
    children,
    currentStep,
    totalSteps,
    showBackButton: t1,
    onBack,
    backButtonLabel: t2,
    isLoggedIn: t3,
    fullWidth: t4
  } = t0;
  const showBackButton = t1 === undefined ? true : t1;
  const backButtonLabel = t2 === undefined ? "Back" : t2;
  const isLoggedIn = t3 === undefined ? false : t3;
  const fullWidth = t4 === undefined ? false : t4;
  const showProgressBar = currentStep > 0;
  const t5 = `flex flex-col bg-white ${fullWidth ? "w-full" : "w-full lg:flex-[0_0_55%]"}`;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      minWidth: 0
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const t7 = `mx-auto w-full px-5 pt-5 sm:px-6 lg:px-0 lg:pt-6 ${fullWidth ? "max-w-5xl" : "max-w-md"}`;
  let t8;
  if ($[1] !== backButtonLabel || $[2] !== currentStep || $[3] !== onBack || $[4] !== showBackButton) {
    t8 = showBackButton && currentStep > 0 && onBack && <motion.button key="back-button" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION} onClick={onBack} className="flex items-center gap-0.5 text-sm font-medium text-black/50 transition-colors hover:text-black"><ChevronLeftIcon className="h-5 w-5" /><span>{backButtonLabel}</span></motion.button>;
    $[1] = backButtonLabel;
    $[2] = currentStep;
    $[3] = onBack;
    $[4] = showBackButton;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t8) {
    t9 = <div className="absolute left-0 top-1/2 -translate-y-1/2"><AnimatePresence mode="wait">{t8}</AnimatePresence></div>;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Link href="/" className="block transition-opacity duration-150 ease-out hover:opacity-70"><Image src="/img/outfame-logo-full.svg" alt="Outfame" width={140} height={32} className="h-7 w-auto" priority={true} /></Link></div>;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== currentStep || $[10] !== isLoggedIn) {
    t11 = !isLoggedIn && currentStep === 0 && <motion.div key="sign-in" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION}><Link href="/login" className="text-sm font-medium text-black/50 transition-colors hover:text-black">Sign in</Link></motion.div>;
    $[9] = currentStep;
    $[10] = isLoggedIn;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11) {
    t12 = <div className="absolute right-0 top-1/2 -translate-y-1/2"><AnimatePresence mode="wait">{t11}</AnimatePresence></div>;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== t12 || $[15] !== t9) {
    t13 = <div className="relative flex h-8 items-center">{t9}{t10}{t12}</div>;
    $[14] = t12;
    $[15] = t9;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== currentStep || $[18] !== isLoggedIn || $[19] !== showProgressBar || $[20] !== totalSteps) {
    t14 = showProgressBar && <motion.div key="progress-bar" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION} className="mt-5"><ProgressBar currentStep={currentStep} totalSteps={totalSteps} isLoggedIn={isLoggedIn} /></motion.div>;
    $[17] = currentStep;
    $[18] = isLoggedIn;
    $[19] = showProgressBar;
    $[20] = totalSteps;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14) {
    t15 = <AnimatePresence mode="wait">{t14}</AnimatePresence>;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t13 || $[25] !== t15 || $[26] !== t7) {
    t16 = <header className={t7}>{t13}{t15}</header>;
    $[24] = t13;
    $[25] = t15;
    $[26] = t7;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = showProgressBar ? "h-6" : "h-5";
  let t18;
  if ($[28] !== t17) {
    t18 = <div className={t17} />;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const t19 = `mx-auto flex w-full flex-1 flex-col ${fullWidth ? "max-w-5xl" : "max-w-md"}`;
  let t20;
  if ($[30] !== children || $[31] !== t19) {
    t20 = <main className="flex flex-1 flex-col px-5 pb-8 sm:px-6 lg:px-10"><div className={t19}>{children}</div></main>;
    $[30] = children;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t16 || $[34] !== t18 || $[35] !== t20 || $[36] !== t5) {
    t21 = <div className={t5} style={t6}>{t16}{t18}{t20}</div>;
    $[33] = t16;
    $[34] = t18;
    $[35] = t20;
    $[36] = t5;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== fullWidth) {
    t22 = !fullWidth && <div className="hidden lg:block" style={{
      width: "45%",
      minWidth: 0
    }}><SocialProofPanel className="h-full" /></div>;
    $[38] = fullWidth;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t21 || $[41] !== t22) {
    t23 = <div className="flex min-h-screen w-full overflow-hidden">{t21}{t22}</div>;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  return t23;
}

/**
 * Fyxer.ai-style segmented progress bar
 *
 * Design specs:
 * - Full width of content area
 * - Rounded pill segments with gaps
 * - Completed + current = filled (black)
 * - Remaining = light gray (black/10)
 * - Smooth width animations
 */
function ProgressBar(t0) {
  const $ = _c(10);
  const {
    currentStep,
    totalSteps,
    isLoggedIn
  } = t0;
  let t1;
  if ($[0] !== isLoggedIn) {
    t1 = step => {
      if (isLoggedIn && step === 3) {
        return 2;
      }
      return step;
    };
    $[0] = isLoggedIn;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getDisplayIndex = t1;
  let t2;
  if ($[2] !== currentStep || $[3] !== getDisplayIndex) {
    t2 = getDisplayIndex(currentStep);
    $[2] = currentStep;
    $[3] = getDisplayIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const displayIndex = t2;
  let t3;
  if ($[5] !== totalSteps) {
    t3 = Array.from({
      length: totalSteps
    });
    $[5] = totalSteps;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== displayIndex || $[8] !== t3) {
    t4 = <div className="flex w-full items-center gap-1.5">{t3.map((_, index) => {
        const isCompleted = index < displayIndex;
        const isCurrent = index === displayIndex;
        const isFilled = isCompleted || isCurrent;
        return <motion.div key={index} className="h-1.5 flex-1 rounded-full" initial={false} animate={{
          backgroundColor: isFilled ? "#141414" : "rgba(0, 0, 0, 0.1)"
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }} />;
      })}</div>;
    $[7] = displayIndex;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}